import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import type { PasswordHistory } from '../types';
import { generatePassword, getNextLine } from '../lib/api';
import PasswordGenerator from '../components/functions/PasswordGenerator';
import LineRequester from '../components/functions/LineRequester';
import AccountUploader from '../components/functions/AccountUploader';
import CallFunction from '../components/functions/CallFunction';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';

const Functions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [passwordHistory, setPasswordHistory] = useState<PasswordHistory[]>([]);
  const [recentLines, setRecentLines] = useState<string[]>([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const loadInitialData = async () => {
      setLoading(true);
      try {
        await fetchPasswordHistory();
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [user, navigate]);

  const fetchPasswordHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('password_history')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      setPasswordHistory(data || []);
    } catch (error) {
      console.error('Error fetching password history:', error);
      throw error;
    }
  };

  const handleGeneratePassword = async () => {
    try {
      const password = await generatePassword();
      await navigator.clipboard.writeText(password);
      toast.success('Password copied to clipboard!');
      await fetchPasswordHistory();
    } catch (error) {
      console.error('Error generating password:', error);
      toast.error('Failed to generate password');
    }
  };

  const handleRequestLine = async () => {
    try {
      const line = await getNextLine();
      await navigator.clipboard.writeText(line);
      setRecentLines(prev => [line, ...prev].slice(0, 3));
      toast.success('Line copied to clipboard');
    } catch (error) {
      console.error('Error requesting line:', error);
      toast.error('Failed to retrieve line');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      <Header />
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
            <PasswordGenerator
              passwordHistory={passwordHistory}
              onGeneratePassword={handleGeneratePassword}
            />
            <LineRequester
              recentLines={recentLines}
              onRequestLine={handleRequestLine}
            />
            <AccountUploader />
            <CallFunction />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Functions;